import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMadFormatter, useTripBookingAdapter } from "../config";
import { formatMad } from "../internal/format";
import { DEFAULT_TRIP_PEOPLE } from "../state/trip-storage";
import { partyOf } from "../lib/trip-party";
import { tripStayLegs, type TripStayLeg } from "../lib/trip-stays";
import { quoteVenueStay } from "../lib/trip-booking";
import type { Trip } from "../types";
import { useStopOffers } from "./use-trip-booking";

/** What one city's stay costs, as far as the plan can say. */
export type LegStayPrice =
  /** No stay chosen, or no nights to sleep. */
  | { readonly state: "none" }
  | { readonly state: "loading" }
  /** Priced for the trip's dates and party. `key` groups amounts that add up. */
  | { readonly state: "priced"; readonly amount: number; readonly key: string; readonly text: string }
  /** Chosen, but the plan cannot price it honestly: its page will. */
  | { readonly state: "page" }
  /** A hotel with no room for those dates and that party. */
  | { readonly state: "full" }
  /** The guest's own place: booked elsewhere, nothing for us to price. */
  | { readonly state: "own" };

export interface TripStayPrices {
  readonly legs: readonly TripStayLeg[];
  readonly prices: readonly LegStayPrice[];
  /** The sum, when every priced stay is in one currency; null otherwise. */
  readonly total: string | null;
}

/**
 * The live price of every stay in a trip: hotels through the host (their
 * cheapest room for those nights and the party, in the guest's currency),
 * camps and villas from their own packages. Paid online, never due on the day.
 */
export function useTripStayPrices(trip: Trip): TripStayPrices {
  const adapter = useTripBookingAdapter();
  const madFormat = useMadFormatter();
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const party = partyOf(trip, DEFAULT_TRIP_PEOPLE);
  const legs = useMemo(() => tripStayLegs(trip), [trip]);

  const hotelLegs = legs.filter((l) => l.stay?.kind === "hotel" && l.nights > 0);
  const hotelResults = useQueries({
    queries: hotelLegs.map((leg) => ({
      queryKey: [
        "trip-stay-quote",
        leg.stay!.slug,
        leg.checkin,
        leg.checkout,
        party.adults,
        party.childAges.join(","),
        lang,
      ] as const,
      queryFn: () =>
        adapter!.fetchStayQuote!({
          slug: leg.stay!.slug,
          checkin: leg.checkin,
          checkout: leg.checkout,
          adults: party.adults,
          childAges: party.childAges,
          lang,
        }),
      enabled: Boolean(adapter?.fetchStayQuote),
      staleTime: 10 * 60 * 1000,
      retry: 1,
    })),
  });

  const venueSlugs = useMemo(
    () => [...new Set(legs.filter((l) => l.stay?.kind === "venue").map((l) => l.stay!.slug))],
    [legs],
  );
  const offerFor = useStopOffers(venueSlugs);

  const money = (mad: number) => (madFormat ? madFormat(mad) : formatMad(mad, lang));
  const prices = legs.map((leg): LegStayPrice => {
    if (!leg.stay || leg.nights < 1) return { state: "none" };
    if (leg.stay.kind === "own") return { state: "own" };
    if (leg.stay.kind === "hotel") {
      if (!adapter?.fetchStayQuote) return { state: "page" };
      const r = hotelResults[hotelLegs.indexOf(leg)];
      if (!r || r.status === "pending") return { state: "loading" };
      if (r.status === "error") return { state: "page" };
      if (!r.data) return { state: "full" };
      const { amount, currency } = r.data;
      const text = adapter.formatStayAmount
        ? adapter.formatStayAmount(amount, currency)
        : `${Math.round(amount)} ${currency}`;
      return { state: "priced", amount, key: currency, text };
    }
    const offer = offerFor?.(leg.stay.slug);
    if (offer === undefined && offerFor) return { state: "loading" };
    const mad = offer ? quoteVenueStay(offer, party, leg.nights) : null;
    if (mad === null) return { state: "page" };
    // Dirhams, shown converted like every other planner price.
    return { state: "priced", amount: mad, key: "MAD", text: money(mad) };
  });

  const priced = prices.filter((p): p is Extract<LegStayPrice, { state: "priced" }> => p.state === "priced");
  // A total only when every chosen stay is priced, in one currency: a sum that
  // leaves one out, or mixes two currencies, is not a number anyone pays.
  const chosen = prices.filter((p) => p.state !== "none" && p.state !== "own").length;
  const oneKey =
    priced.length > 0 && priced.length === chosen && priced.every((p) => p.key === priced[0]!.key);
  let total: string | null = null;
  if (oneKey && priced.length > 1) {
    const sum = priced.reduce((s, p) => s + p.amount, 0);
    const key = priced[0]!.key;
    total =
      key === "MAD"
        ? money(sum)
        : adapter?.formatStayAmount
          ? adapter.formatStayAmount(sum, key)
          : `${Math.round(sum)} ${key}`;
  } else if (oneKey) {
    total = priced[0]!.text;
  }
  return { legs, prices, total };
}
